/**
 * Máquina de estados pura de la sesión de participante (sin efectos ni DOM).
 * Los efectos (cámara, red, temporizadores) viven en `ParticipantPage` y solo despachan eventos.
 */
import type { InterruptionReason } from "@gazelab/shared";

export type ExperimentState =
  | "WELCOME"
  | "CONSENT"
  | "PROFILE"
  | "PREFLIGHT"
  | "FULLSCREEN"
  | "PRACTICE"
  | "CALIBRATION"
  | "VALIDATION"
  | "PURSUIT"
  | "READY"
  | "FIXATION_CROSS"
  | "STIMULUS"
  | "TRIAL_SAVE"
  | "PAUSE"
  | "COMPLETE"
  | "ABORTED"
  | "INTERRUPTED";

export interface MachineContext {
  state: ExperimentState;
  /** La sesión existe en servidor (tras pantalla completa). */
  sessionReady: boolean;
  totalImages: number;
  /** Índice (en el orden del protocolo) de la siguiente imagen a mostrar. */
  nextIndex: number;
  revalidateEvery: number;
  trialsSinceValidation: number;
  practiceEnabled: boolean;
  practiceDone: boolean;
  /** Validación con punto en movimiento tras la primera validación aceptada (una vez por sesión). */
  pursuitEnabled: boolean;
  pursuitDone: boolean;
  /** Fase con la que se registrará la próxima validación. */
  validationPhase: "initial" | "revalidation";
  /** Hay una validación aceptada vigente (se pierde con cualquier interrupción). */
  validated: boolean;
  /** Tras una interrupción: hay que repetir pantalla completa + calibración + validación. */
  resuming: boolean;
  pauseRequested: boolean;
  interruption: InterruptionReason | null;
  abortReason: InterruptionReason | null;
  completedTrials: number;
  interruptedTrials: number;
}

export type MachineEvent =
  | { type: "START" }
  | { type: "CONSENT_ACCEPTED" }
  | { type: "PROFILE_DONE" }
  | { type: "PREFLIGHT_OK" }
  | { type: "SESSION_READY"; totalImages: number; revalidateEvery: number; practiceEnabled: boolean; pursuitEnabled?: boolean }
  | { type: "FULLSCREEN_OK" }
  | { type: "PRACTICE_DONE" }
  | { type: "CALIBRATION_DONE" }
  | { type: "VALIDATION_ACCEPTED" }
  | { type: "VALIDATION_RECALIBRATE" }
  | { type: "PURSUIT_DONE" }
  | { type: "BEGIN" }
  | { type: "FIXATION_DONE" }
  | { type: "STIMULUS_DONE" }
  | { type: "TRIAL_SAVED" }
  | { type: "REQUEST_PAUSE" }
  | { type: "RESUME_FROM_PAUSE" }
  | { type: "INTERRUPT"; reason: InterruptionReason }
  | { type: "RESUME" }
  | { type: "ABORT"; reason: InterruptionReason }
  | { type: "FINISH" };

export function initialContext(): MachineContext {
  return {
    state: "WELCOME",
    sessionReady: false,
    totalImages: 0,
    nextIndex: 0,
    revalidateEvery: 0,
    trialsSinceValidation: 0,
    practiceEnabled: false,
    practiceDone: false,
    pursuitEnabled: false,
    pursuitDone: false,
    validationPhase: "initial",
    validated: false,
    resuming: false,
    pauseRequested: false,
    interruption: null,
    abortReason: null,
    completedTrials: 0,
    interruptedTrials: 0,
  };
}

/** Estados en los que la cámara/captura está activa y una interrupción debe detenerla. */
export const CAPTURE_STATES: ReadonlySet<ExperimentState> = new Set([
  "PRACTICE",
  "CALIBRATION",
  "VALIDATION",
  "PURSUIT",
  "READY",
  "FIXATION_CROSS",
  "STIMULUS",
  "TRIAL_SAVE",
  "PAUSE",
]);

/** Estados de presentación en los que no puede haber ninguna UI superpuesta. */
export const PRESENTATION_STATES: ReadonlySet<ExperimentState> = new Set(["FIXATION_CROSS", "STIMULUS", "TRIAL_SAVE"]);

export const TERMINAL_STATES: ReadonlySet<ExperimentState> = new Set(["COMPLETE", "ABORTED"]);

/** Interrupciones que requieren reinicializar la cámara (volver a PREFLIGHT). */
const CAMERA_REASONS: ReadonlySet<InterruptionReason> = new Set(["camera_lost", "permission_lost"]);

function go(ctx: MachineContext, state: ExperimentState, patch: Partial<MachineContext> = {}): MachineContext {
  return { ...ctx, ...patch, state };
}

/** Tras guardar un ensayo (o tras validar), decide el siguiente paso. */
function afterTrial(ctx: MachineContext): MachineContext {
  if (ctx.nextIndex >= ctx.totalImages) return go(ctx, "COMPLETE", { pauseRequested: false });
  if (ctx.revalidateEvery > 0 && ctx.trialsSinceValidation >= ctx.revalidateEvery) {
    return go(ctx, "VALIDATION", { validationPhase: "revalidation", validated: false });
  }
  if (ctx.pauseRequested) return go(ctx, "PAUSE", { pauseRequested: false });
  return go(ctx, "FIXATION_CROSS");
}

export function reducer(ctx: MachineContext, ev: MachineEvent): MachineContext {
  if (TERMINAL_STATES.has(ctx.state)) return ctx;

  switch (ev.type) {
    case "ABORT":
      return go(ctx, "ABORTED", { abortReason: ev.reason });
    case "INTERRUPT": {
      if (!CAPTURE_STATES.has(ctx.state) && ctx.state !== "FULLSCREEN") return ctx;
      // Una imagen interrumpida ya fue (parcialmente) vista: no se repite.
      const wasStimulus = ctx.state === "STIMULUS";
      return go(ctx, "INTERRUPTED", {
        interruption: ev.reason,
        validated: false,
        resuming: true,
        pauseRequested: false,
        nextIndex: wasStimulus ? ctx.nextIndex + 1 : ctx.nextIndex,
        interruptedTrials: wasStimulus ? ctx.interruptedTrials + 1 : ctx.interruptedTrials,
      });
    }
    default:
      break;
  }

  switch (ctx.state) {
    case "WELCOME":
      return ev.type === "START" ? go(ctx, "CONSENT") : ctx;
    case "CONSENT":
      return ev.type === "CONSENT_ACCEPTED" ? go(ctx, "PROFILE") : ctx;
    case "PROFILE":
      return ev.type === "PROFILE_DONE" ? go(ctx, "PREFLIGHT") : ctx;
    case "PREFLIGHT":
      return ev.type === "PREFLIGHT_OK" ? go(ctx, "FULLSCREEN") : ctx;
    case "FULLSCREEN":
      if (ev.type === "SESSION_READY" && !ctx.sessionReady) {
        const withSession: MachineContext = {
          ...ctx,
          sessionReady: true,
          totalImages: ev.totalImages,
          revalidateEvery: ev.revalidateEvery,
          practiceEnabled: ev.practiceEnabled,
          pursuitEnabled: ev.pursuitEnabled ?? false,
        };
        if (ev.totalImages === 0) return go(withSession, "COMPLETE");
        return ev.practiceEnabled ? go(withSession, "PRACTICE") : go(withSession, "CALIBRATION");
      }
      if (ev.type === "FULLSCREEN_OK" && ctx.sessionReady) {
        return go(ctx, ctx.practiceEnabled && !ctx.practiceDone && !ctx.resuming ? "PRACTICE" : "CALIBRATION");
      }
      return ctx;
    case "PRACTICE":
      return ev.type === "PRACTICE_DONE" ? go(ctx, "CALIBRATION", { practiceDone: true }) : ctx;
    case "CALIBRATION":
      return ev.type === "CALIBRATION_DONE" ? go(ctx, "VALIDATION") : ctx;
    case "VALIDATION":
      if (ev.type === "VALIDATION_RECALIBRATE") return go(ctx, "CALIBRATION", { validated: false });
      if (ev.type === "VALIDATION_ACCEPTED") {
        const accepted = { ...ctx, validated: true, trialsSinceValidation: 0, validationPhase: "revalidation" as const };
        // Primera validación o reanudación: (validación con punto en movimiento, una sola vez) y
        // luego instrucción y botón de inicio. Revalidación intermedia: seguir.
        if (ctx.validationPhase === "initial" || ctx.resuming) {
          const next = ctx.pursuitEnabled && !ctx.pursuitDone ? "PURSUIT" : "READY";
          return go(accepted, next, { resuming: false, interruption: null });
        }
        return afterTrial(accepted);
      }
      return ctx;
    case "PURSUIT":
      return ev.type === "PURSUIT_DONE" ? go(ctx, "READY", { pursuitDone: true }) : ctx;
    case "READY":
      if (ev.type === "BEGIN") return ctx.nextIndex >= ctx.totalImages ? go(ctx, "COMPLETE") : go(ctx, "FIXATION_CROSS");
      return ctx;
    case "FIXATION_CROSS":
      if (ev.type === "FIXATION_DONE") return go(ctx, "STIMULUS");
      if (ev.type === "REQUEST_PAUSE") return { ...ctx, pauseRequested: true };
      return ctx;
    case "STIMULUS":
      if (ev.type === "STIMULUS_DONE") return go(ctx, "TRIAL_SAVE");
      if (ev.type === "REQUEST_PAUSE") return { ...ctx, pauseRequested: true };
      return ctx;
    case "TRIAL_SAVE":
      if (ev.type === "TRIAL_SAVED") {
        return afterTrial({
          ...ctx,
          nextIndex: ctx.nextIndex + 1,
          trialsSinceValidation: ctx.trialsSinceValidation + 1,
          completedTrials: ctx.completedTrials + 1,
        });
      }
      if (ev.type === "REQUEST_PAUSE") return { ...ctx, pauseRequested: true };
      return ctx;
    case "PAUSE":
      return ev.type === "RESUME_FROM_PAUSE" ? go(ctx, "FIXATION_CROSS") : ctx;
    case "INTERRUPTED":
      if (ev.type === "RESUME") {
        if (ctx.nextIndex >= ctx.totalImages && ctx.sessionReady) return go(ctx, "COMPLETE");
        const needsCamera = ctx.interruption !== null && CAMERA_REASONS.has(ctx.interruption);
        return go(ctx, needsCamera ? "PREFLIGHT" : "FULLSCREEN", { validationPhase: "revalidation" });
      }
      if (ev.type === "FINISH" && ctx.nextIndex >= ctx.totalImages) return go(ctx, "COMPLETE");
      return ctx;
    default:
      return ctx;
  }
}
